import {
  type IPostTrainingAppliesCandidateBody,
  type IPostTrainingAppliesCandidateResponseItem,
  getAxiosErrorMessage,
  usePostTrainingAppliesCandidate,
} from "@api";
import { useCandidateDetailsContext } from "@candidate/details/context";
import { useEffect, useRef } from "react";
import { type DefaultValues, useForm, useWatch } from "react-hook-form";
import { createTrainingSharedDefaultValues } from "../../sharedForm/defaults";
import { isTrainingOfferAddressRequired } from "../../sharedForm/rules";
import type { IModalTrainingApplyForm } from "../types";
import { useModalTrainingApplyDocuments } from "./documents";
import {
  formatTrainingApplyFormToManualOffer,
  formatTrainingApplyFormToOffer,
  formatTrainingApplyPayload,
  formatTrainingApplyPayloadWithOfferCreation,
} from "./format";
import { type TModalTrainingApplyFormResolver, formResolver } from "./resolver";

const BASE_DEFAULT_VALUES: DefaultValues<IModalTrainingApplyForm> = {
  ...createTrainingSharedDefaultValues(),
  status: "",
  createAssociatedOffer: false,
  documents: [],
};

export const useModalTrainingApply = (
  open: boolean,
  defaultValues: DefaultValues<IModalTrainingApplyForm> | undefined,
  onClose: () => void,
  onCreate?: (createdTrainingApply: IPostTrainingAppliesCandidateResponseItem) => void
) => {
  const { candidate } = useCandidateDetailsContext();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [{ loading: loadingCreateApply }, postTrainingAppliesCandidate] = usePostTrainingAppliesCandidate();
  const { documentsProgress, handleTrainingApplyDocuments } = useModalTrainingApplyDocuments();

  const hookForm = useForm<IModalTrainingApplyForm, void, TModalTrainingApplyFormResolver>({
    defaultValues: { ...BASE_DEFAULT_VALUES, ...defaultValues },
    // biome-ignore lint/suspicious/noExplicitAny: TO-DO : Uprgrade @hookform/resolvers to v5 and remove the `as any`
    resolver: formResolver as any,
  });

  const remote = useWatch({ control: hookForm.control, name: "remote" });
  const required = isTrainingOfferAddressRequired(remote);

  useEffect(() => {
    if (!required) {
      hookForm.trigger("address");
    }
  }, [hookForm, required]);

  useEffect(() => {
    if (!open) {
      return;
    }

    hookForm.reset({ ...BASE_DEFAULT_VALUES, ...defaultValues });
  }, [hookForm, open, defaultValues]);

  const createTrainingApply = async (
    payload: IPostTrainingAppliesCandidateBody
  ): Promise<IPostTrainingAppliesCandidateResponseItem> => {
    const response = await postTrainingAppliesCandidate(candidate._id, [payload]);
    const createdTrainingApply = response.data[0];
    if (!createdTrainingApply) {
      throw new Error("La candidature formation a été créée, mais aucune donnée n'a été retournée par l'API.");
    }
    return createdTrainingApply;
  };

  const handleSubmit = hookForm.handleSubmit(async (data) => {
    try {
      const documents = await handleTrainingApplyDocuments(data.documents);
      const offerPayload = formatTrainingApplyFormToOffer(data, documents);
      let createdTrainingApply: IPostTrainingAppliesCandidateResponseItem;
      if (data.createAssociatedOffer) {
        createdTrainingApply = await createTrainingApply(
          formatTrainingApplyPayloadWithOfferCreation(data, offerPayload)
        );
      } else {
        const manualOffer = formatTrainingApplyFormToManualOffer(data, documents);
        createdTrainingApply = await createTrainingApply(formatTrainingApplyPayload(data, manualOffer));
      }

      window.publish("feedback", {
        severity: "success",
        message: "La candidature formation a été créée avec succès.",
      });
      onCreate?.(createdTrainingApply);
      onClose();
    } catch (error) {
      window.publish("feedback", {
        severity: "error",
        message:
          (error instanceof Error ? error.message : undefined) ||
          getAxiosErrorMessage(error) ||
          "Une erreur est survenue lors de la création de la candidature formation. Veuillez contacter un administrateur.",
      });
    }
  });

  return {
    hookForm,
    formRef,
    handleSubmit,
    documentsProgress,
    loading: loadingCreateApply || Boolean(documentsProgress),
  };
};
